import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Plus } from "lucide-react";

export type BlockType =
  | "feature"
  | "point"
  | "multipoint"
  | "route"
  | "remotegeojson";

interface AddBlockDropdownProps {
  onAddBlock: (blockType: BlockType) => void;
  allowedTypes: BlockType[];
  label?: string;
  size?: "sm" | "default" | "lg";
  variant?:
    | "default"
    | "outline"
    | "secondary"
    | "ghost"
    | "link"
    | "destructive";
}

const blockTypeLabels: Record<BlockType, string> = {
  feature: "Feature",
  point: "Point",
  multipoint: "Multi Point",
  route: "Route",
  remotegeojson: "Remote GeoJSON/GPX",
};

const blockTypeDescriptions: Record<BlockType, string> = {
  feature: "Geographic feature with properties",
  point: "Single point location",
  multipoint: "Multiple point locations",
  route: "Route with waypoints",
  remotegeojson: "Remote GeoJSON or GPX file fetched by URL",
};

export default function AddBlockDropdown({
  onAddBlock,
  allowedTypes,
  label = "Add Block",
  size = "default",
  variant = "default",
}: AddBlockDropdownProps) {
  // If only one type is allowed, create a simple button that adds it directly
  if (allowedTypes.length === 1) {
    const blockType = allowedTypes[0];
    if (!blockType) return null;
    return (
      <Button
        variant={variant}
        size={size}
        onClick={() => onAddBlock(blockType)}
      >
        <Plus className="h-4 w-4 mr-2" />
        {label}
      </Button>
    );
  }

  // Multiple types - show dropdown
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant={variant} size={size}>
          <Plus className="h-4 w-4 mr-2" />
          {label}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {allowedTypes.map((blockType) => (
          <DropdownMenuItem
            key={blockType}
            onClick={() => onAddBlock(blockType)}
            className="flex flex-col items-start"
          >
            <span className="font-medium">{blockTypeLabels[blockType]}</span>
            <span className="text-xs text-muted-foreground">
              {blockTypeDescriptions[blockType]}
            </span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
